import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  Server,
  Brain,
  Cloud,
  Shield,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePackageOfferings } from "@/providers/ContentProvider";
import { SolutionTypeDialog } from "./SolutionTypeDialog";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getIconForOffering(offering: PackageOfferingDto) {
  const name = offering.name.toLowerCase();
  if (name.includes("mcp") || name.includes("gateway")) return Server;
  if (name.includes("knowledge") || name.includes("engine")) return Brain;
  if (name.includes("guardian")) return Shield;
  return Cloud;
}

function PackageCard({
  offering,
  onSelect,
}: {
  offering: PackageOfferingDto;
  onSelect: (offering: PackageOfferingDto) => void;
}) {
  const Icon = getIconForOffering(offering);
  const isRecurring = offering.pricing.type === "RECURRING";

  return (
    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
      <Card className="glass-card border-gradient h-full flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <CardTitle className="text-lg">{offering.name}</CardTitle>
            </div>
            <Badge variant="secondary" className="shrink-0">
              {offering.timelineEstimate}
            </Badge>
          </div>
          <CardDescription className="line-clamp-2">
            {offering.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="space-y-2 mb-4">
            {offering.pricing.priceRange && (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-primary">
                  {formatCurrency(offering.pricing.priceRange.min)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {isRecurring ? "/ mo USD" : "USD"}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="text-[10px]">
                {isRecurring ? "Monthly retainer" : "Flat fee"}
              </Badge>
              {offering.depositConfig.required && (
                <Badge variant="outline" className="text-[10px]">
                  {offering.depositConfig.depositPercentage}% deposit
                </Badge>
              )}
              {!offering.depositConfig.required && offering.depositConfig.setupFee && (
                <Badge variant="outline" className="text-[10px]">
                  {formatCurrency(offering.depositConfig.setupFee)} setup
                </Badge>
              )}
              {offering.cloudSupport?.available && (
                <Badge variant="secondary" className="text-[10px]">
                  Cloud support available
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-auto pt-4">
            <Button className="w-full" onClick={() => onSelect(offering)}>
              {offering.ctaText || "Get Started"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function PortalServicesPage() {
  const { offerings, isLoading, error } = usePackageOfferings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<PackageOfferingDto | null>(null);

  const handleSelectPackage = (offering: PackageOfferingDto) => {
    setSelectedPackage(offering);
    setDialogOpen(true);
  };

  const handleCustomSolution = () => {
    setSelectedPackage(null);
    setDialogOpen(true);
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">
          Choose Your Solution
        </h1>
        <p className="text-muted-foreground">
          Select from the service packages or create a custom solution
          tailored to your needs.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Card className="glass-card border-gradient border-primary/30 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
          <CardContent className="relative p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-semibold text-xl mb-2">
                  Need something custom?
                </h3>
                <p className="text-muted-foreground">
                  Have a unique project? Let's build a solution tailored
                  specifically to your requirements.
                </p>
              </div>
              <Button
                size="lg"
                onClick={handleCustomSolution}
                className="shrink-0"
              >
                Create a custom solution
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center py-24">
          <p className="text-destructive">{error}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {offerings.map((offering) => (
            <PackageCard
              key={offering.id}
              offering={offering}
              onSelect={handleSelectPackage}
            />
          ))}
        </div>
      )}

      <SolutionTypeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedPackage={selectedPackage}
      />
    </div>
  );
}
